// Named transitions of the view state (ADR-0017).
//
// A transition earns a name here once two places have to make it the same way.
// `viewState.set(patch, mode)` stays the general writer — this module is not a
// façade over it — and the caller goes on owning `'open'` vs `'replace'`
// (ADR-0009) everywhere except where a command owns the whole transition,
// which is the point of naming it.
//
// `state/` may import only `shared/` (ADR-0013), so a fact that lives in a part
// arrives as a parameter. That is why `returnToNow` is told whether we know
// where the resident is instead of reading `kreska/geolocate.ts`.
import { here } from '../shared/geo.ts';
import type { Point } from './clamp.ts';
import { nearestPlaceName } from './districts.ts';
import { type Centre, viewState } from './viewstate.ts';

/**
 * Do we have the resident's position? The one fact `returnToNow` needs, and
 * the one both callers can reach: `geolocate` is the kreska's module and the
 * wheel may not import it (ADR-0013), while `here` is shared.
 *
 * It is also the fact `resolveCentre` itself reads, so a centre of
 * `{ kind: 'me' }` chosen on it can never resolve to a position we do not
 * have: the target and what the target means stay one decision (§5.8).
 */
export const located = (): boolean => here.at !== null;

/**
 * Where „ja, teraz" is. Pure, and the ONE target rule both controls obey
 * (owner's ruling, 2026-09-20): the resident where we know them, Stary Rynek
 * where we do not — denied, never asked and unavailable being one outcome.
 */
export function nowCentre(known: boolean): Centre {
  return known ? { kind: 'me' } : { kind: 'default' };
}

/**
 * The ONE history mode both controls obey. A return is an opening: ADR-0009
 * files a centre move under `'open'`, and this moves the centre.
 *
 * `'replace'` was tried and reversed, for three costs that are not worth an
 * „honest close" (PR #277):
 *
 *  1. A `'replace'` write is folded into `carried` and merged onto whatever
 *     entry the tab is next given, until the next opening clears it — so the
 *     return **overwrote the centre of the next address to arrive**, and a link
 *     someone sent was silently rewritten back to `tu=ja`.
 *  2. Back became inert: the return was re-applied onto each older entry in
 *     turn, so two Backs changed nothing and the third left the app.
 *  3. At depth 0 — a shared link, this session having pushed nothing — there
 *     was no entry to give back at all, and Back went to `about:blank` with the
 *     sender's centre unrecoverable.
 *
 * `carried` exists for changes made INSIDE A SCREEN (ADR-0009, D-b). A return
 * to now is not one. With `'open'`, Back undoes the return and lands on the
 * place the resident left, which is an honest undo.
 */
const RETURNING = 'open' as const;

/**
 * „Wróć do: ja, teraz" — the wheel's crosshair and the kreska's `◇`, one rule
 * for both (#226). Before this they disagreed twice over: the crosshair pushed
 * an entry and landed on Stary Rynek, the `◇` replaced one and landed on the
 * resident.
 *
 * @param known whether we have the resident's position — {@link located}.
 */
export function returnToNow(known: boolean): void {
  // `at: null` with it, always: coming home is a move in space, and a move in
  // space returns the clock to its own now (D-p).
  viewState.set({ centre: nowCentre(known), at: null }, RETURNING);
}

/**
 * The name the kreska reads for a centre nothing better can name.
 *
 * A moved centre is named after the nearest district centroid
 * (`nearestPlaceName`, `./districts.ts`, SPEC-viewfinder §1.5). This is what is
 * left when the nearest one is further than 3 km — `nearestPlaceName` answers
 * `null` there rather than spelling the string a second time.
 */
const PICKED_NAME = 'wybrane miejsce';

/**
 * „Stąd" — the ONE writer of a centre the resident picked (SPEC-viewfinder
 * §1.4). Three callers, one rule: a settled pan, „Stąd" on a report, and
 * „Stąd, o 15:00" on an event.
 *
 * A centre move is an opening, so Back unwinds it (ADR-0009, §1.7: one
 * finished gesture is exactly one entry).
 *
 * `at` is written **every time**, `null` included, and that is the whole
 * reason it is a parameter rather than a constant: „stąd" means here and
 * **now** (D-p), so a borrowed 15:00 left behind by „Stąd, o 15:00" must be
 * cleared by a plain move — `viewState.set` is a merge, and a field this call
 * does not name survives it. „Stąd, o 15:00" passes a real hour and keeps it.
 *
 * `also.radius` is the one thing a gesture adds and a detail's „Stąd" never
 * has: a pinch pans around its two-finger midpoint, so a gesture that changed
 * BOTH writes them together, once, under the one `'open'` the centre earns
 * (§1.1). Two writes would be two Backs for one finished gesture.
 *
 * It also NAMES the centre, and that is §1.5's rule for every move rather than
 * for a gesture (#312): a caller with a better name — „Stąd" on a report, which
 * says the fault's own title — passes it, and a caller with none gets the
 * nearest district, or {@link PICKED_NAME} past 3 km. Naming it here is what
 * makes a drag, the empty-ring sentence's tap and a `sel=` beyond the ring read
 * the same, without three callers remembering the same rule — two of which
 * could not reach it at all, `axis/` being barred from `wheel/` (ADR-0013).
 *
 * @param centre where the resident put the middle of the circle.
 * @param at the borrowed hour to stand at, `null` for now.
 * @param also the name the kreska reads — left out for „let the place name
 *   itself" — and a radius the same gesture settled on.
 */
export function moveCentre(
  centre: Point,
  at: string | null = null,
  also: { name?: string; radius?: number } = {},
): void {
  const { name, radius } = also;
  const named = name ?? nearestPlaceName(centre) ?? PICKED_NAME;
  const patch = { centre: { kind: 'picked', ...centre, name: named } as Centre, at };
  viewState.set(radius === undefined ? patch : { ...patch, radius }, 'open');
}
